import { c as _c } from "react/compiler-runtime";
import { IcoArrowUpRight16, IcoNetwork16, IcoPasskey16 } from '@onefootprint/icons';
import { getErrorMessage } from '@onefootprint/request';
import type { ApiError, AuthMethodKind, GraphOperationUpdateSettings, PlaybookSettings } from '@onefootprint/request-types/dashboard';
import { Form, InlineAlert, LinkButton } from '@onefootprint/ui';
import Link from 'next/link';
import { type ReactNode, useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import useCanModernize from '../../../../../use-can-modernize';
import type { FormData } from '../../../playbook-settings.types';
import SettingsModernize from '../settings-modernize';
export type SettingsDocumentFormProps = {
  playbook: PlaybookSettings;
  onSave?: (data: Omit<GraphOperationUpdateSettings, 'kind'>) => void;
  saveError: ApiError | null;
  setIsFormDirty: (isDirty: boolean) => void;
  modernize?: ReactNode;
};
const SettingsDocumentForm = t0 => {
  const $ = _c(52);
  const {
    playbook,
    onSave,
    saveError,
    setIsFormDirty,
    modernize
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "settingsDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const canModernize = useCanModernize();
  const t2 = !onSave;
  const t3 = playbook.isModern;
  const t4 = playbook.requiresAuthentication;
  let t5;
  if ($[1] !== playbook.requiredAuthMethods) {
    t5 = playbook.requiredAuthMethods.includes("phone");
    $[1] = playbook.requiredAuthMethods;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== playbook.requiredAuthMethods) {
    t6 = playbook.requiredAuthMethods.includes("email");
    $[3] = playbook.requiredAuthMethods;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== t5 || $[6] !== t6) {
    t7 = {
      phone: t5,
      email: t6
    };
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== playbook.isModern || $[9] !== playbook.promptForPasskey || $[10] !== playbook.requiresAuthentication || $[11] !== t7) {
    t8 = {
      isModern: t3,
      requiresAuthentication: t4,
      requiredAuthMethods: t7,
      promptForPasskey: playbook.promptForPasskey
    };
    $[8] = playbook.isModern;
    $[9] = playbook.promptForPasskey;
    $[10] = playbook.requiresAuthentication;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t2 || $[14] !== t8) {
    t9 = {
      disabled: t2,
      defaultValues: t8
    };
    $[13] = t2;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const formMethods = useForm(t9);
  const {
    control,
    handleSubmit,
    formState
  } = formMethods;
  let t10;
  if ($[16] !== control) {
    t10 = {
      control,
      name: "isModern"
    };
    $[16] = control;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const isModern = useWatch(t10);
  let t11;
  if ($[18] !== control) {
    t11 = {
      control,
      name: "requiresAuthentication"
    };
    $[18] = control;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const requiresAuthentication = useWatch(t11);
  let t12;
  if ($[20] !== formState.dirtyFields || $[21] !== setIsFormDirty) {
    t12 = () => {
      setIsFormDirty(Object.keys(formState.dirtyFields).length > 0);
    };
    $[20] = formState.dirtyFields;
    $[21] = setIsFormDirty;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== formState || $[24] !== setIsFormDirty) {
    t13 = [formState, setIsFormDirty];
    $[23] = formState;
    $[24] = setIsFormDirty;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  useEffect(t12, t13);
  let t14;
  if ($[26] !== canModernize || $[27] !== onSave) {
    t14 = async data => {
      if (!onSave) {
        console.error("No onSave function provided in settings-document-form");
        return;
      }
      const getRequiredAuthMethods = () => {
        const methods = [];
        if (!data.requiresAuthentication) {
          return methods;
        }
        if (data.requiredAuthMethods.phone) {
          methods.push("phone");
        }
        if (data.requiredAuthMethods.email) {
          methods.push("email");
        }
        return methods;
      };
      const isModern_0 = canModernize ? data.isModern : undefined;
      if (isModern_0) {
        onSave({
          isModern: isModern_0,
          requiresAuthentication: false,
          requiredAuthMethods: [],
          promptForPasskey: false
        });
        return;
      }
      onSave({
        isModern: isModern_0,
        requiresAuthentication: data.requiresAuthentication,
        requiredAuthMethods: getRequiredAuthMethods(),
        promptForPasskey: data.promptForPasskey
      });
    };
    $[26] = canModernize;
    $[27] = onSave;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const handleFormSubmit = t14;
  let t15;
  if ($[29] !== handleFormSubmit || $[30] !== handleSubmit) {
    t15 = handleSubmit(handleFormSubmit);
    $[29] = handleFormSubmit;
    $[30] = handleSubmit;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t) {
    t16 = t(_temp);
    $[32] = t;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== saveError) {
    t17 = saveError && <InlineAlert variant="error">{getErrorMessage(saveError)}</InlineAlert>;
    $[34] = saveError;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== isModern || $[37] !== requiresAuthentication || $[38] !== t) {
    t18 = !isModern && <><div className="flex flex-col gap-4"><div className="flex flex-col gap-1"><div className="flex gap-1 items-center"><IcoPasskey16 /><div className="text-label-2 text-primary">{t(_temp2)}</div></div><div className="text-body-2 text-secondary">{t(_temp3)}</div></div><div className="flex flex-col gap-1"><Form.Toggle name="promptForPasskey" size="compact" label={<span className="text-label-2">{t(_temp4)}</span>} /><div className="flex flex-col gap-1 pl-11 text-body-2 text-tertiary"><p>{t(_temp5)}</p><p>{t(_temp6)}</p><div className="flex flex-wrap items-center gap-x-2 gap-y-1"><LinkButton href="https://developer.apple.com/passkeys/" target="_blank" variant="label-2" iconComponent={IcoArrowUpRight16}>{t(_temp7)}</LinkButton><LinkButton href="https://developer.android.com/design/ui/mobile/guides/patterns/passkeys" target="_blank" variant="label-2" iconComponent={IcoArrowUpRight16}>{t(_temp8)}</LinkButton></div></div></div></div><div className="flex flex-col gap-4"><div className="flex flex-col gap-1"><div className="flex gap-1 items-center"><IcoNetwork16 /><div className="text-label-2 text-primary">{t(_temp9)}</div></div><div className="text-body-2 text-secondary">{t(_temp10)}</div></div><Form.Toggle name="requiresAuthentication" label={t(_temp11)} size="compact" /></div>{!requiresAuthentication && <InlineAlert variant="neutral"><Trans ns="playbook-builder" i18nKey={_temp12} components={{
          onboardingSessionLink: <Link href="https://docs.onefootprint.com/articles/integrate/onboarding-sessions#example-use-cases-guarantee-resolution-to-a-specific-user" target="_blank" className="text-label-2 text-accent hover:text-accent-hover">{t(_temp13)}</Link>,
          fpId: <code className="align-baseline">{t(_temp14)}</code>,
          userExternalId: <code className="align-baseline">{t(_temp15)}</code>
        }} /></InlineAlert>}</>;
    $[36] = isModern;
    $[37] = requiresAuthentication;
    $[38] = t;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== canModernize || $[41] !== modernize) {
    t19 = canModernize && <SettingsModernize>{modernize}</SettingsModernize>;
    $[40] = canModernize;
    $[41] = modernize;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t15 || $[44] !== t16 || $[45] !== t17 || $[46] !== t18 || $[47] !== t19) {
    t20 = <form onSubmit={t15} id="settings-form" className="flex flex-col gap-8" aria-label={t16}>{t17}{t18}{t19}</form>;
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== formMethods || $[50] !== t20) {
    t21 = <FormProvider {...formMethods}>{t20}</FormProvider>;
    $[49] = formMethods;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  return t21;
};
export default SettingsDocumentForm;
function _temp($) {
  return $.documentForm.ariaLabel;
}
function _temp2($_0) {
  return $_0.passkeys.label;
}
function _temp3($_1) {
  return $_1.passkeys.subtitle;
}
function _temp4($_2) {
  return $_2.passkeys.enablePasskeyRegistration;
}
function _temp5($_3) {
  return $_3.passkeys.description;
}
function _temp6($_4) {
  return $_4.passkeys.about.title;
}
function _temp7($_5) {
  return $_5.passkeys.about.ios;
}
function _temp8($_6) {
  return $_6.passkeys.about.android;
}
function _temp9($_7) {
  return $_7.authentication.shortTitle;
}
function _temp10($_8) {
  return $_8.authentication.description;
}
function _temp11($_9) {
  return $_9.authentication.form.enable.label;
}
function _temp12($_10) {
  return $_10.settingsDialog.otp.noAuthenticationInfo;
}
function _temp13($_11) {
  return $_11.otp.onboardingSession;
}
function _temp14($_12) {
  return $_12.otp.fpId;
}
function _temp15($_13) {
  return $_13.otp.userExternalId;
}